/**
 * Backs the visitor's display preferences up to their account, and brings them
 * down on a new device.
 *
 * Mounted once at the root, inside the auth provider. Being an effect is
 * load-bearing the same way it is for useFavoritesSync: effects never run
 * during a server render, so this is also the guarantee that no account data
 * reaches the SSR output and that the first client render matches it. There is
 * no cookie to make an SSR document account-aware — the Firebase ID token lives
 * in JS memory — so a preference that arrives from the server can only ever
 * arrive after first paint.
 *
 * That is visible exactly once per browser: an adopt-remote that changes the
 * theme repaints late. It only happens when this browser is still showing the
 * defaults (see chooseReconciliation), and the adopt writes localStorage, so
 * the next load's blocking ThemeScript already has the right answer.
 */

import { useEffect } from 'react'
import { useAuth } from 'lib/auth/auth-context'
import { getUserSettings, subscribeToUserSettings } from 'services/user.service'
import { markPrefsSynced, needsPush, pushLocalPrefs, reconcilePrefs } from 'services/prefs.service'

const PUSH_DEBOUNCE_MS = 500

export function usePrefsSync(): void {
   const { state } = useAuth()

   // Keyed on the uid, not just the status: signing out of one account and into
   // another inside a single SPA session never passes through 'signed-out' as a
   // rendered state.
   const uid = state.status === 'signed-in' ? state.user.uid : null

   useEffect(() => {
      // Signing out deliberately does nothing. Favorites are cleared because
      // they are somebody else's data; preferences are this browser's display
      // settings, and blanking them would flip the theme on the way out.
      if (!uid) return
      void reconcilePrefs(uid)
   }, [uid])

   useEffect(() => {
      if (!uid) return

      let timer: ReturnType<typeof setTimeout> | undefined
      const unsubscribe = subscribeToUserSettings(() => {
         const prefs = getUserSettings()
         // The listener also fires for preferences this browser just *received*
         // from the server, which would otherwise be echoed straight back.
         if (!needsPush(prefs)) return

         clearTimeout(timer)
         timer = setTimeout(() => {
            void pushLocalPrefs(prefs).then((ok) => {
               if (ok) markPrefsSynced(prefs)
            })
         }, PUSH_DEBOUNCE_MS)
      })

      return () => {
         clearTimeout(timer)
         unsubscribe()
      }
   }, [uid])
}
